/**
 * Damage-type parsing — the single place that knows how a damage type is written.
 *
 * items.json records a weapon's damage type in one of two mutually exclusive ways,
 * mirroring how the rulebooks print it:
 *
 *   1. an explicit Title Case `weaponStats.damageType` — "Energy", "Rending", …
 *   2. a trailing letter on `weaponStats.damage` — "1d10 R", "1d5-1 I"
 *
 * Only `mono-sword` currently carries both. 92 entries use form 2 alone, and
 * before this parser existed they were all silently treated as Impact.
 *
 * A third group is neither: weapons whose profile is prose-described rather than
 * a damage roll (webbers, graviton guns, shock nets) store "Special", "Snare" or
 * "Varies". Those are NOT errors and must not be coerced into a DamageType — see
 * PROSE_DAMAGE_PROFILES.
 */

import { DamageType } from '../types/dice';

/** Rulebook single-letter damage codes, as printed after the dice expression. */
export const DAMAGE_TYPE_LETTERS: Readonly<Record<string, DamageType>> = {
  I: DamageType.Impact,
  R: DamageType.Rending,
  E: DamageType.Energy,
  X: DamageType.Explosive
};

/**
 * Values that legitimately appear where a damage type would go, but describe a
 * weapon with no damage roll at all. These are real source data, not defects.
 */
export const PROSE_DAMAGE_PROFILES: readonly string[] = ['special', 'snare', 'varies'];

/** Matches a rulebook damage letter at the end of a dice expression, e.g. "1d10+2 R". */
const DAMAGE_LETTER_RE = /\d\s*([IREX])\b/;

/**
 * Resolve a written damage-type name to a DamageType.
 *
 * Case-insensitive, because items.json stores Title Case ("Energy") while the
 * enum values are lowercase. A trailing parenthetical is ignored so that
 * "Impact (Variable)" resolves to Impact — the damage type is still Impact, the
 * parenthetical qualifies how much.
 *
 * Returns undefined for prose profiles and anything unrecognised.
 */
export function parseDamageTypeName(value?: string | null): DamageType | undefined {
  if (!value) return undefined;
  const bare = value
    .replace(/\s*\(.*\)\s*$/, '')
    .trim()
    .toLowerCase();
  const match = Object.values(DamageType).find((dt) => dt === bare);
  return match;
}

/**
 * Extract the damage type from a damage expression's trailing letter.
 * "1d10 R" -> Rending. Returns undefined when there is no letter.
 */
export function parseDamageTypeLetter(damage?: string | null): DamageType | undefined {
  if (!damage) return undefined;
  const m = DAMAGE_LETTER_RE.exec(damage);
  return m ? DAMAGE_TYPE_LETTERS[m[1]] : undefined;
}

/**
 * Resolve a weapon's damage type from either notation, preferring the explicit
 * field. Returns undefined when the weapon has no damage type — callers that
 * need a concrete value should apply their own documented default (DiceService
 * uses `?? DamageType.Impact`).
 */
export function resolveDamageType(stats?: {
  damageType?: string | null;
  damage?: string | null;
}): DamageType | undefined {
  if (!stats) return undefined;
  return parseDamageTypeName(stats.damageType) ?? parseDamageTypeLetter(stats.damage);
}

/** True when the value is a documented prose profile rather than a damage type. */
export function isProseDamageProfile(value?: string | null): boolean {
  if (!value) return false;
  return PROSE_DAMAGE_PROFILES.includes(value.trim().toLowerCase());
}

/**
 * Render a DamageType in the Title Case form items.json uses ("Rending").
 * Lets writers of items.json-shaped data derive the spelling from the enum
 * instead of hand-maintaining another letter -> name table.
 */
export function damageTypeName(damageType: DamageType): string {
  return damageType.charAt(0).toUpperCase() + damageType.slice(1);
}
